export type LeakCategory = {
  slug: string;
  name: string;
  desc: string;
  count: number;
};

export type LeakTimelineItem = {
  date: string;
  text: string;
};

export type LeakEntry = {
  id: string;
  code: string;
  title: string;
  category: string;
  categorySlug: string;
  source: string;
  level: string;
  date: string;
  time: string;
  readTime: string;
  summary: string;
  tags: string[];
  views: number;
  replies: number;
  cover: string;
  keyPoints: string[];
  body: string[];
  timeline: LeakTimelineItem[];
};

export const leakCategories: LeakCategory[] = [
  { slug: "film", name: "影视剧组", desc: "拍摄现场、排期、合同与后期环节的现场观察", count: 68 },
  { slug: "variety", name: "综艺选秀", desc: "赛制、剪辑与现场录制的流程类线索", count: 43 },
  { slug: "stage", name: "演出舞台", desc: "巡演排期、票务流转与场馆执行的细节", count: 37 },
  { slug: "music", name: "音乐产业", desc: "制作、版权、发行与合作模式的行业动向", count: 29 },
  { slug: "backstage", name: "幕后职场", desc: "一线岗位的用工、薪资与工作强度实录", count: 52 },
  { slug: "brand", name: "品牌风云", desc: "商务代言、联名与投放策略的幕后讨论", count: 24 },
];

export const leakLevels = ["已核验", "多方印证", "待核验", "线索汇总"];

export const leakEntries: LeakEntry[] = [
  {
    id: "night-001",
    code: "NL-2608-001",
    title: "某古装大制作剧组：连续夜戏跨 36 小时，餐补按「天」结算引发场务集体申诉",
    category: "影视剧组",
    categorySlug: "film",
    source: "剧组场务人员",
    level: "多方印证",
    date: "2026-08-28",
    time: "23:47",
    readTime: "7 分钟",
    summary:
      "线人提供的三日排期表显示，主演组夜戏从傍晚六点连拍至次日中午，中间仅安排两次各二十分钟的转场休息。争议集中在跨天夜戏的餐补计算方式——无论拍到几点都只按一个自然日结算。",
    tags: ["剧组排期", "劳动强度", "餐补结算"],
    views: 18420,
    replies: 236,
    cover:
      "https://readdy.ai/api/search-image?query=moody%20cinematic%20night%20film%20set%20with%20warm%20amber%20worklights%20and%20silhouetted%20crew%20members%2C%20deep%20shadow%2C%20film%20grain%2C%20editorial%20documentary%20photography%2C%20dark%20warm%20tones&width=1200&height=800&seq=leak-film-set-night-01&orientation=landscape",
    keyPoints: [
      "三日排期显示主演组夜戏累计 36 小时，跨两个自然日",
      "餐补按「开机日」单次结算，未按实际跨越天数补发",
      "线人称已有十余位场务联合提交书面申诉",
      "制片方暂未就该结算方式作出公开说明",
    ],
    body: [
      "这份线索最早出现在凌晨一点的投稿后台，附件是三张手机翻拍的纸质排期表。表上的时间被红色马克笔圈出好几处，旁边写着「又通宵」。",
      "从排期表看，主演组的夜戏从傍晚六点开始，一直排到次日中午十二点，中间仅两次各二十分钟的转场休息。按照常规的工时口径，这段时间已经跨越了两个自然日。",
      "争议的核心并不在拍摄本身，而在结算方式。多位线人提到，剧组的餐补与加班补贴统一按「开机日」计算，无论实际拍到几点，都只计一次。这意味着跨天拍摄的一线人员，实际拿到的是单日标准。",
      "我们随后联系到第二位线人，对方的说法与排期表基本吻合，并补充称已有十余名场务联合提交了书面申诉，目前仍在等待答复。",
      "需要说明的是，本文所述排期与结算方式来自多位线人的独立描述，尚未获得制片方的正式回应。若后续有新的说明，我们会在这条线索下补充更新。",
    ],
    timeline: [
      { date: "08-26", text: "首位线人通过匿名入口提交排期表照片" },
      { date: "08-27", text: "第二位线人独立印证夜戏时长与休息安排" },
      { date: "08-28", text: "线索进入「多方印证」等级并对外发布" },
    ],
  },
  {
    id: "night-002",
    code: "NL-2608-002",
    title: "某选秀节目内部评分表流出：现场投票权重与正片剪辑顺序存在明显出入",
    category: "综艺选秀",
    categorySlug: "variety",
    source: "节目组实习人员",
    level: "已核验",
    date: "2026-08-24",
    time: "01:12",
    readTime: "6 分钟",
    summary:
      "投稿者提供的评分表包含每位选手的现场票数、导师分与最终加权结果。对比正片播出顺序后可以发现，现场票数领先的选手在剪辑中并未获得对应的镜头权重。",
    tags: ["赛制", "剪辑顺序", "现场票数"],
    views: 26310,
    replies: 412,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20television%20studio%20control%20room%20with%20glowing%20monitors%20and%20scoring%20sheets%20on%20a%20desk%2C%20moody%20warm%20lighting%2C%20editorial%20photography%2C%20cinematic%20atmosphere&width=1200&height=800&seq=leak-variety-score-02&orientation=landscape",
    keyPoints: [
      "评分表包含现场票数、导师分与加权结果三列数据",
      "现场票数前三的选手，正片中单期镜头时长排名并不靠前",
      "剪辑顺序与录制顺序存在多处调换",
      "节目组回应称剪辑仅服务于叙事节奏，不影响赛果",
    ],
    body: [
      "评分表的拍照件一共七张，覆盖了本季前四期的录制。表格结构并不复杂：选手编号、现场票数、导师分、加权结果，四列并排。",
      "真正引起我们注意的是第二列与正片之间的差距。现场票数排名前三的选手，在已播出的两期里，单人镜头时长分别排在第 6、第 9 和第 4 位。",
      "更细的差异出现在剪辑顺序上。线人指出，第二期的开场表演在录制时排在整场的中段，正片中被移到了最前面，理由是「开场需要更强的节奏」。",
      "我们就此向节目组求证，得到的回复是：剪辑顺序仅服务于叙事节奏，不参与任何赛果计算，现场投票的统计口径从未改变。",
      "这条线索之所以被评为「已核验」，是因为评分表中的加权算法可以被独立复算——我们用表格中的原始数据重算了一遍，结果与表格给出的加权得分完全一致。",
    ],
    timeline: [
      { date: "08-22", text: "投稿者一次性提交七张评分表拍照件" },
      { date: "08-23", text: "编辑室独立复算加权算法，结果一致" },
      { date: "08-24", text: "节目组作出简短回应，线索发布" },
    ],
  },
  {
    id: "night-003",
    code: "NL-2608-003",
    title: "音乐节后台排期表曝光：黄金时段之外，还藏着一份「保底场次」名单",
    category: "演出舞台",
    categorySlug: "stage",
    source: "巡演执行人员",
    level: "待核验",
    date: "2026-08-19",
    time: "00:38",
    readTime: "5 分钟",
    summary:
      "线人提供的一份后台排期表显示，除公开海报上的阵容外，另有一份不在宣传物料中的场次安排，用于安置合作方的指定艺人。",
    tags: ["音乐节", "排期", "场次安排"],
    views: 12980,
    replies: 154,
    cover:
      "https://readdy.ai/api/search-image?query=backstage%20of%20an%20outdoor%20music%20festival%20at%20night%20with%20warm%20strings%20of%20lights%20and%20schedule%20boards%2C%20atmospheric%20dark%20tones%2C%20editorial%20documentary%20photography&width=1200&height=800&seq=leak-festival-stage-03&orientation=landscape",
    keyPoints: [
      "后台排期表包含一份未对外公布的补充场次",
      "补充场次集中在夜间八点后的黄金区间",
      "线人称安置对象与商务合作直接相关",
      "目前尚无第二位线人独立印证该名单",
    ],
    body: [
      "这份排期表是用铅笔手写的，纸角有明显的折痕。表头写着「现场执行版」，与对外发布的海报阵容相比，多出了四行用括号标注的场次。",
      "按照线人的说法，这四行是给合作方指定艺人预留的位置，不进入公开宣传物料，也不占用海报上的曝光顺序。",
      "从时间分布看，这四场都落在晚上八点到十点之间——也就是音乐节人流与声量最高的区间。",
      "我们尝试联系第二位线人进行印证，截至发布时只有一位执行人员回应，且未能提供完整名单。因此这条线索目前维持「待核验」等级。",
      "补充一句：排期表的真实性并不等同于名单的真实性，后续如果出现第二位独立来源，我们会更新这条线索的等级。",
    ],
    timeline: [
      { date: "08-18", text: "线人提交手写排期表照片" },
      { date: "08-19", text: "未能获得第二位独立来源，标记为待核验" },
    ],
  },
  {
    id: "night-004",
    code: "NL-2608-004",
    title: "剧集番位之争的另一面：同组演员的合同条款，究竟差在哪几行",
    category: "影视剧组",
    categorySlug: "film",
    source: "制片统筹助理",
    level: "线索汇总",
    date: "2026-08-15",
    time: "23:05",
    readTime: "8 分钟",
    summary:
      "多份匿名投稿指向同一个现象：番位排序之争往往不是情绪问题，而是合同里几条具体条款的外在表现。我们把这些零散描述汇总成了一份可读的对照。",
    tags: ["合同条款", "番位", "制片流程"],
    views: 21740,
    replies: 305,
    cover:
      "https://readdy.ai/api/search-image?query=close%20up%20of%20stacked%20contract%20pages%20and%20a%20fountain%20pen%20on%20a%20warm%20wooden%20desk%20under%20a%20single%20lamp%2C%20dark%20warm%20editorial%20photography%2C%20film%20grain&width=1200&height=800&seq=leak-contract-desk-04&orientation=landscape",
    keyPoints: [
      "争议常集中在署名顺序、海报位次与宣传物料露出三条",
      "署名条款通常与档期绑定，而非单纯的人气排序",
      "海报位次往往在开机前就已写进补充协议",
      "汇总结论来自多份线索的共性描述，非单一信源",
    ],
    body: [
      "关于番位的讨论，外界往往停留在情绪层面。但过去两个月里，我们收到了至少五份来自不同组别的投稿，指向的却是同一类具体条款。",
      "第一类是署名顺序。线人普遍提到，署名并不完全按人气排，很多时候与档期绑定——能配合完整拍摄周期的演员，在署名上会获得更靠前的位置。",
      "第二类是海报位次。多份线索显示，海报上的头像大小与排列顺序，通常在开机前的补充协议里就已写明，后期修改需要各方重新走一遍确认流程。",
      "第三类是宣传物料露出，包括预告片时长、发布会站位等。这部分最容易产生争议，因为它既受合同约束，又受现场实际情况影响。",
      "需要强调的是，本篇属于「线索汇总」，结论来自多份匿名描述的共性部分，并非针对任何单一项目。我们把它整理出来，是希望把讨论拉回到可核对的条款层面。",
    ],
    timeline: [
      { date: "07-30", text: "收到第一份关于署名条款的投稿" },
      { date: "08-08", text: "累计五份不同组别的相关线索" },
      { date: "08-15", text: "整理为汇总型内容并发布" },
    ],
  },
  {
    id: "night-005",
    code: "NL-2607-005",
    title: "「坑位费」报价单浮出水面：同一位主播为何有三档完全不同的价格",
    category: "品牌风云",
    categorySlug: "brand",
    source: "品牌方媒介采购",
    level: "多方印证",
    date: "2026-07-31",
    time: "22:19",
    readTime: "6 分钟",
    summary:
      "一份来自品牌方的采购报价单显示，同一场直播的坑位费存在三档报价，差异取决于口播时长、上架顺序以及是否跳转独立链接。",
    tags: ["直播合作", "报价单", "商务投放"],
    views: 16230,
    replies: 188,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20studio%20desk%20with%20product%20samples%2C%20a%20ring%20light%20and%20printed%20price%20lists%2C%20warm%20amber%20accents%2C%20moody%20editorial%20photography&width=1200&height=800&seq=leak-livestream-pricing-05&orientation=landscape",
    keyPoints: [
      "同一场直播存在三档坑位费报价",
      "价格差异与口播时长、上架顺序强相关",
      "跳转独立链接的版本价格最高",
      "品牌方与执行方对报价口径的描述基本一致",
    ],
    body: [
      "这份报价单的格式很朴素：一行一品类，后面跟着价格区间。真正让品牌方困惑的是，同一场直播、同一个位置，却出现了三个不同的数字。",
      "线人的解释是，这三个数字对应三种不同的交付方式。基础档只包含一次口播，标准档增加了产品演示与两分钟黄金上架位，最高档则提供独立跳转链接。",
      "我们向另一位执行侧线人求证，对方给出的口径与报价单基本一致，并补充说三档之间的差价通常在两倍左右。",
      "对品牌方而言，问题不在于价格本身，而在于报价结构没有被事先说清楚——很多采购方直到复盘时才发现，自己买到的是基础档。",
    ],
    timeline: [
      { date: "07-28", text: "品牌方线人提交报价单拍照件" },
      { date: "07-30", text: "执行侧线人独立印证三档结构" },
      { date: "07-31", text: "线索发布，标记为多方印证" },
    ],
  },
  {
    id: "night-006",
    code: "NL-2607-006",
    title: "综艺录制现场的「补录」机制：为什么同一段掌声会被反复录制七遍",
    category: "综艺选秀",
    categorySlug: "variety",
    source: "现场执行观众",
    level: "已核验",
    date: "2026-07-24",
    time: "01:41",
    readTime: "5 分钟",
    summary:
      "多位观众线人描述了同一套录制流程：现场导演会在嘉宾发言结束后统一补录反应镜头，掌声、欢呼与惊讶表情都需要单独拍摄。",
    tags: ["录制流程", "补录", "现场执行"],
    views: 19560,
    replies: 267,
    cover:
      "https://readdy.ai/api/search-image?query=empty%20television%20studio%20audience%20seating%20under%20warm%20spotlights%20at%20night%2C%20dark%20atmospheric%20editorial%20photography%2C%20cinematic%20tones&width=1200&height=800&seq=leak-studio-audience-06&orientation=landscape",
    keyPoints: [
      "反应镜头与发言内容分开录制",
      "同一段掌声最多补录七遍",
      "观众收到明确的动作指令，包括鼓掌节奏",
      "该流程属行业常规，并非单一节目特例",
    ],
    body: [
      "这条线索来自四位互不相识的现场观众，他们分别在不同场次录过节目，却描述了几乎一模一样的流程。",
      "按他们的说法，嘉宾的发言是连续录完的，但观众的反应镜头需要事后单独补录。现场执行会给出指令：先鼓掌，再欢呼，然后是统一的惊讶表情。",
      "一位线人记得最清楚的是，同一段掌声被要求录了七遍，理由是「前几次镜头的机位不对」。",
      "需要客观说明的是，补录反应镜头是电视制作的通用手段，本身并不涉及信息失真。我们发布这条线索，是为了让观众理解成片与现场之间的正常差异。",
    ],
    timeline: [
      { date: "07-20", text: "首位观众线人描述补录流程" },
      { date: "07-23", text: "另外三位线人独立印证相同细节" },
      { date: "07-24", text: "标记为已核验并发布" },
    ],
  },
  {
    id: "night-007",
    code: "NL-2607-007",
    title: "剧组深夜收工后的另一场「加班」：道具归还与场景复原为何总被算在下班之后",
    category: "幕后职场",
    categorySlug: "backstage",
    source: "道具组工作人员",
    level: "多方印证",
    date: "2026-07-18",
    time: "00:06",
    readTime: "6 分钟",
    summary:
      "多位幕后岗位线人指向同一现象：正式收工后的道具归还、场景复原与车辆调度，通常不纳入当日工时统计。",
    tags: ["工时统计", "道具组", "幕后岗位"],
    views: 14120,
    replies: 173,
    cover:
      "https://readdy.ai/api/search-image?query=prop%20workshop%20at%20night%20with%20wooden%20crates%2C%20hand%20tools%20and%20a%20single%20warm%20hanging%20lamp%2C%20dark%20moody%20editorial%20photography&width=1200&height=800&seq=leak-props-warehouse-07&orientation=landscape",
    keyPoints: [
      "收工后的复原工作普遍不计入当日工时",
      "道具组、置景组与车辆调度受影响最明显",
      "线人称该惯例在多个组别中普遍存在",
      "缺少书面工时记录是维权难点",
    ],
    body: [
      "「收工」这两个字，在剧组里其实有两层意思。对多数人来说，它指的是拍摄停止；但对道具、置景和车辆岗位来说，工作才刚开始。",
      "三位来自不同组别的线人都提到，正式收工之后还有一段固定的复原时间：道具清点归还、场景恢复原状、车辆重新调度，通常在一到三小时之间。",
      "问题在于，这段时间普遍不进入当日工时统计。线人给出的原因很实际——现场没有统一的打卡机制，工时以场记单为准，而场记单只记录拍摄。",
      "缺少书面记录，是这类问题最难推进的部分。我们把这条线索整理出来，并不是要指认某个组别，而是希望把「收工之后」这段时间纳入讨论。",
    ],
    timeline: [
      { date: "07-12", text: "首位道具组线人提交工时描述" },
      { date: "07-16", text: "置景与车辆岗位线人分别印证" },
      { date: "07-18", text: "线索发布" },
    ],
  },
  {
    id: "night-008",
    code: "NL-2607-008",
    title: "OST 合作模式的悄悄转向：从一次性买断到「分阶段结算」，制作方怎么算这笔账",
    category: "音乐产业",
    categorySlug: "music",
    source: "音乐制作人",
    level: "待核验",
    date: "2026-07-09",
    time: "02:14",
    readTime: "7 分钟",
    summary:
      "线人称部分影视项目的原声合作正在从一次性买断转向分阶段结算，付款节点与剧集上线节奏绑定。",
    tags: ["原声合作", "结算模式", "版权"],
    views: 9880,
    replies: 96,
    cover:
      "https://readdy.ai/api/search-image?query=analog%20recording%20studio%20at%20night%20with%20mixing%20console%2C%20warm%20vintage%20lamps%20and%20sheet%20music%2C%20dark%20editorial%20photography&width=1200&height=800&seq=leak-music-studio-08&orientation=landscape",
    keyPoints: [
      "部分项目采用分阶段结算，与上线节奏绑定",
      "首期款比例较以往的买断模式明显降低",
      "制作方对结算周期的敏感度上升",
      "该说法目前仅有单一来源，尚未印证",
    ],
    body: [
      "这位线人在音乐制作行业待了十一年，他的判断是：OST 的合作方式正在发生变化，只是变化发生得比较安静。",
      "他描述的新模式是分阶段结算——签约后支付首期款，剧集上线支付第二期，播出结束再结清尾款。相比过去的一次性买断，首期款的比例低了不少。",
      "对制作方来说，这种模式的吸引力在于现金流压力被摊薄；对创作者来说，风险则转移到了「剧集能否顺利上线」这个不可控变量上。",
      "需要说明，这条线索目前只有一位线人提供，我们尚未找到第二位独立来源进行印证，因此维持「待核验」等级。如果你掌握相关情况，可以从下方入口投稿。",
    ],
    timeline: [
      { date: "07-07", text: "制作人线人提交合作模式描述" },
      { date: "07-09", text: "未获第二位来源印证，标记为待核验" },
    ],
  },
];